import React, { useState, useEffect } from 'react';
import { 
  Calendar as CalendarIcon, 
  Phone, 
  Mail, 
  MapPin, 
  ChevronLeft, 
  ChevronRight, 
  CheckCircle2, 
  Clock, 
  AlertCircle,
  Loader2,
  Copy,
  ExternalLink,
  ShieldCheck,
  Lock,
  ChevronDown
} from 'lucide-react';
import { api } from '../../api/client';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import { formatUzPhone, cleanUzPhone, isCompleteUzPhone, capitalizeWords, localizeDayOffReason, localizeLawyerPosition } from '../../utils/helpers';
import { showToast } from '../common/Toast';
import AuthModal from '../auth/AuthModal';
import BookingConfirmModal from './BookingConfirmModal';
import SubscriptionModal from '../subscription/SubscriptionModal';

export default function IntegratedBookingSection({ 
  preSelectedService = null, 
  preSelectedTopic = null,
  initialLawyerId = null,
  compact = false,
  showBack = false,
  onSuccess = null,
  onBack = null 
}) {
  const { user, isAuthenticated, refreshUser, isSubscribed } = useAuth();
  const { t, localized, currentLang } = useLanguage();
  const [subscriptionModalOpen, setSubscriptionModalOpen] = useState(false);

  // Tashkent / Local Date and Time helpers
  const getTodayStr = () => {
    const d = new Date();
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const getCurrentTimeStr = () => {
    const d = new Date();
    const hours = String(d.getHours()).padStart(2, '0');
    const minutes = String(d.getMinutes()).padStart(2, '0');
    return `${hours}:${minutes}`;
  };

  // Calendar state
  const today = new Date();
  const [currentMonth, setCurrentMonth] = useState(today.getMonth());
  const [currentYear, setCurrentYear] = useState(today.getFullYear());
  
  // Find next potential working day (skipping weekend as initial heuristic)
  const getNextPotentialWorkingDate = () => {
    const base = new Date();
    for (let i = 1; i <= 14; i++) {
      const candidate = new Date();
      candidate.setDate(base.getDate() + i);
      const dow = candidate.getDay();
      if (dow !== 0 && dow !== 6) {
        const y = candidate.getFullYear();
        const m = String(candidate.getMonth() + 1).padStart(2, '0');
        const d = String(candidate.getDate()).padStart(2, '0');
        return `${y}-${m}-${d}`;
      }
    }
    const d = new Date();
    d.setDate(d.getDate() + 1);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  };

  const [selectedDate, setSelectedDate] = useState(getNextPotentialWorkingDate);
  const [weeklySchedule, setWeeklySchedule] = useState([]);
  
  // Slot state
  const [selectedTime, setSelectedTime] = useState('10:00');
  const [availableSlots, setAvailableSlots] = useState([
    { time: '09:00', label: '09:00 AM', available: true },
    { time: '09:30', label: '09:30 AM', available: true },
    { time: '10:00', label: '10:00 AM', available: true },
    { time: '10:30', label: '10:30 AM', available: true },
    { time: '11:00', label: '11:00 AM', available: true },
    { time: '11:30', label: '11:30 AM', available: true },
    { time: '12:00', label: '12:00 PM', available: true },
    { time: '12:30', label: '12:30 PM', available: true },
    { time: '13:00', label: '01:00 PM', available: true },
    { time: '13:30', label: '01:30 PM', available: true },
    { time: '14:00', label: '02:00 PM', available: true },
    { time: '14:30', label: '02:30 PM', available: true },
    { time: '15:00', label: '03:00 PM', available: true },
    { time: '15:30', label: '03:30 PM', available: true },
    { time: '16:00', label: '04:00 PM', available: true },
    { time: '16:30', label: '04:30 PM', available: true },
    { time: '17:00', label: '05:00 PM', available: true },
    { time: '17:30', label: '05:30 PM', available: true },
  ]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [closedReason, setClosedReason] = useState('');

  // Form state
  const [services, setServices] = useState([]);
  const [lawyers, setLawyers] = useState([]);
  const [offices, setOffices] = useState([]);
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('+998 ()');
  const [serviceId, setServiceId] = useState('');
  const [lawyerId, setLawyerId] = useState('');
  const [details, setDetails] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [bookingSuccess, setBookingSuccess] = useState(null);

  // Modals state
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);

  // Auto-fill from authenticated client
  useEffect(() => {
    if (user) {
      if (user.fullName) setFullName(user.fullName);
      if (user.phone) setPhone(formatUzPhone(user.phone));
      if (user.email) setEmail(user.email);
    }
  }, [user]);

  // Fetch initial services, lawyers, offices, and weekly working schedule
  useEffect(() => {
    Promise.all([
      api.get('/services'),
      api.get('/lawyers'),
      api.get('/offices'),
      api.get('/working-hours?officeId=1'),
    ]).then(([sRes, lRes, oRes, whRes]) => {
      if (sRes?.success && sRes.services) {
        setServices(sRes.services);
        if (sRes.services[0]) {
          setServiceId(String(sRes.services[0].id));
        }
      }
      if (lRes?.success && lRes.lawyers) {
        const activeLawyers = lRes.lawyers.filter((l) => Boolean(l.isActive) === true);
        setLawyers(activeLawyers);
        if (initialLawyerId) {
          setLawyerId(String(initialLawyerId));
        } else if (activeLawyers[0]) {
          setLawyerId(String(activeLawyers[0].id));
        }
      }
      if (oRes?.success && oRes.offices) setOffices(oRes.offices);
      if (whRes?.success && Array.isArray(whRes.workingHours)) {
        setWeeklySchedule(whRes.workingHours);
      }
    }).catch((err) => {
      console.warn('Initial booking meta error:', err);
    });
  }, [initialLawyerId]);

  useEffect(() => {
    if (initialLawyerId) {
      setLawyerId(String(initialLawyerId));
    }
  }, [initialLawyerId]);

  // Update pre-selected service if provided
  useEffect(() => {
    if (preSelectedService && services.length > 0) {
      const match = services.find((s) => s.id === preSelectedService.id || s.slug === preSelectedService.slug);
      if (match) setServiceId(String(match.id));
    } else if (preSelectedTopic && services.length > 0) {
      const match = services.find((s) => 
        s.titleUz?.toLowerCase().includes(preSelectedTopic.title?.toLowerCase() || '') ||
        preSelectedTopic.title?.toLowerCase().includes(s.titleUz?.toLowerCase() || '')
      );
      if (match) setServiceId(String(match.id));
    }
  }, [preSelectedService, preSelectedTopic, services]);

  const getDayScheduleInfo = (year, month, day) => {
    const d = new Date(year, month, day);
    const dayOfWeek = d.getDay(); // 0 = Sun, 1 = Mon, ..., 6 = Sat
    const sched = weeklySchedule.find((s) => s.dayOfWeek === dayOfWeek);
    const isWorking = sched ? Boolean(sched.isOpen) : (dayOfWeek !== 0 && dayOfWeek !== 6);
    return {
      dayOfWeek,
      isWorking,
      openTime: sched?.openTime || '09:00',
      closeTime: sched?.closeTime || '18:00',
    };
  };

  // When weeklySchedule is fetched, adjust selectedDate if it lands on a closed day
  useEffect(() => {
    if (!weeklySchedule || weeklySchedule.length === 0) return;
    const [y, m, d] = (selectedDate || '').split('-').map(Number);
    if (y && m && d) {
      const info = getDayScheduleInfo(y, m - 1, d);
      if (!info.isWorking) {
        const curr = new Date();
        for (let i = 1; i <= 14; i++) {
          const cand = new Date();
          cand.setDate(curr.getDate() + i);
          const candInfo = getDayScheduleInfo(cand.getFullYear(), cand.getMonth(), cand.getDate());
          if (candInfo.isWorking) {
            const nextDateStr = `${cand.getFullYear()}-${String(cand.getMonth() + 1).padStart(2, '0')}-${String(cand.getDate()).padStart(2, '0')}`;
            setSelectedDate(nextDateStr);
            setCurrentMonth(cand.getMonth());
            setCurrentYear(cand.getFullYear());
            break;
          }
        }
      }
    }
  }, [weeklySchedule]);

  // Load dynamic slots when date or lawyer changes
  useEffect(() => {
    if (!selectedDate) return;
    const lId = lawyerId || lawyers[0]?.id;
    // Don't fetch until lawyer list is loaded (avoids double flash on mount)
    if (!lId && lawyers.length === 0) return;

    const todayStr = getTodayStr();
    const currentTimeStr = getCurrentTimeStr();
    const isToday = selectedDate === todayStr;

    if (selectedDate < todayStr) {
      setAvailableSlots([]);
      setSelectedTime('');
      setClosedReason('Bu sana o‘tib ketgan, iltimos boshqa sanani tanlang');
      setLoadingSlots(false);
      return;
    }

    setLoadingSlots(true);
    setClosedReason('');
    
    const oId = offices[0]?.id || 1;

    api.get(`/public/slots?lawyerId=${lId}&officeId=${oId}&date=${selectedDate}`)
      .then((res) => {
        if (res?.success && Array.isArray(res.slots) && res.slots.length > 0) {
          const formatted = res.slots.map((s) => {
            const isSlotPast = isToday && s.time <= currentTimeStr;
            return {
              time: s.time,
              label: formatTo12Hour(s.time),
              available: s.available && !isSlotPast,
              reason: isSlotPast ? 'Bu vaqt oralig‘i o‘tib ketgan' : s.reason,
            };
          });
          setAvailableSlots(formatted);
          const firstAvail = formatted.find((s) => s.available);
          if (firstAvail) {
            setSelectedTime(firstAvail.time);
            setClosedReason('');
          } else {
            setSelectedTime('');
            setClosedReason(isToday ? 'Bugungi kundagi barcha qabul vaqtlari o‘tib ketgan yoki band' : 'Ushbu kunda barcha qabul vaqtlari band');
          }
        } else if (res?.isOpen === false) {
          setAvailableSlots([]);
          setSelectedTime('');
          const cleanReason = res.reason
            ? res.reason.replace(/\s*\([^\)]*qabul[^\)]*\)/gi, '').replace(/\s*—\s*qabul\s*yo[‘'`]?q/gi, '').trim()
            : 'Dam olish kuni';
          setClosedReason(cleanReason);
        } else {
          setAvailableSlots([]);
          setSelectedTime('');
          setClosedReason('Ushbu kunda qabul vaqtlari mavjud emas');
        }
      })
      .catch((err) => {
        console.warn('Slots load error:', err);
        setAvailableSlots([]);
        setSelectedTime('');
        setClosedReason('Qabul vaqtlari yuklanmadi');
      })
      .finally(() => setLoadingSlots(false));
  }, [selectedDate, lawyerId, lawyers, offices]);

  function formatTo12Hour(time24) {
    if (!time24) return '';
    const [h, m] = time24.split(':').map(Number);
    const period = h >= 12 ? 'PM' : 'AM';
    const h12 = h % 12 || 12;
    return `${String(h12).padStart(2, '0')}:${String(m).padStart(2, '0')} ${period}`;
  }

  // Days in month calculation for calendar
  const daysInMonth = new Date(currentYear, currentMonth + 1, 0).getDate();
  const firstDayIndex = new Date(currentYear, currentMonth, 1).getDay();
  const monthNamesByLang = {
    uz: ['Yanvar', 'Fevral', 'Mart', 'Aprel', 'May', 'Iyun', 'Iyul', 'Avgust', 'Sentyabr', 'Oktyabr', 'Noyabr', 'Dekabr'],
    ru: ['Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь', 'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь'],
    en: ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']
  };
  const dayNamesByLang = {
    uz: ['Ya', 'Du', 'Se', 'Ch', 'Pa', 'Ju', 'Sh'],
    ru: ['Вс', 'Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб'],
    en: ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa']
  };
  const monthNames = monthNamesByLang[currentLang] || monthNamesByLang.uz;
  const dayNames = dayNamesByLang[currentLang] || dayNamesByLang.uz;

  const isPrevMonthDisabled = currentYear < today.getFullYear() || (currentYear === today.getFullYear() && currentMonth <= today.getMonth());

  const handlePrevMonth = () => {
    if (isPrevMonthDisabled) return;
    if (currentMonth === 0) {
      setCurrentMonth(11);
      setCurrentYear((y) => y - 1);
    } else {
      setCurrentMonth((m) => m - 1);
    }
  };

  const handleNextMonth = () => {
    if (currentMonth === 11) {
      setCurrentMonth(0);
      setCurrentYear((y) => y + 1);
    } else {
      setCurrentMonth((m) => m + 1);
    }
  };

  const handleSelectDay = (day) => {
    const formatted = `${currentYear}-${String(currentMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    const todayStr = getTodayStr();
    if (formatted < todayStr) {
      showToast('Bu sana o‘tib ketgan, iltimos boshqa sanani tanlang!', 'error');
      return;
    }
    setSelectedDate(formatted);
  };

  // Step 1: Pre-validation & opening confirmation modal
  const handlePreSubmit = (e) => {
    e.preventDefault();

    // Check if user is authenticated (Gatekeeper rule)
    if (!isAuthenticated) {
      showToast('Ariza topshirish uchun avval tizimga kiring yoki ro‘yxatdan o‘ting', 'info');
      setAuthModalOpen(true);
      return;
    }

    const todayStr = getTodayStr();
    const currentTimeStr = getCurrentTimeStr();

    if (selectedDate < todayStr) {
      showToast('Bu sana o‘tib ketgan, iltimos boshqa sanani tanlang!', 'error');
      return;
    }

    if (selectedDate === todayStr && selectedTime && selectedTime <= currentTimeStr) {
      showToast('Bu vaqt oralig‘i o‘tib ketgan, iltimos boshqa vaqtni tanlang!', 'error');
      return;
    }

    if (!fullName.trim() || fullName.trim().length < 2) {
      showToast('Ismingizni to‘liq kiriting', 'error');
      return;
    }
    if (!isCompleteUzPhone(phone)) {
      showToast(currentLang === 'uz' ? 'Telefon raqamini to‘liq kiriting: +998 (97) 325-07-70' : currentLang === 'ru' ? 'Введите полный номер телефона: +998 (97) 325-07-70' : 'Please enter full phone number: +998 (97) 325-07-70', 'error');
      return;
    }
    if (!details.trim() || details.trim().length < 10) {
      showToast('Murojaat sababi kamida 10 ta belgidan iborat bo‘lishi shart', 'error');
      return;
    }

    if (!selectedTime) {
      showToast(closedReason || 'Iltimos, qabul vaqtini tanlang. Agar ushbu kun band bo‘lsa, boshqa sanani tanlang.', 'error');
      return;
    }

    // Open confirmation dialog
    setConfirmModalOpen(true);
  };

  // Step 2: Final submit upon user's explicit confirmation in modal
  const handleConfirmedSubmit = async () => {
    if (submitting) return;

    const todayStr = getTodayStr();
    const currentTimeStr = getCurrentTimeStr();
    if (selectedDate < todayStr || (selectedDate === todayStr && selectedTime <= currentTimeStr)) {
      showToast('Bu sana yoki vaqt oralig‘i o‘tib ketgan, iltimos boshqa sana va vaqtni tanlang!', 'error');
      setConfirmModalOpen(false);
      return;
    }

    setSubmitting(true);
    try {
      const selectedServiceObj = services.find((s) => String(s.id) === String(serviceId)) || services[0];
      const selectedLawyerObj = lawyers.find((l) => String(l.id) === String(lawyerId)) || lawyers[0] || { id: 1 };
      const assignedOffice = offices[0] || { id: 1 };

      const payload = {
        clientName: fullName.trim(),
        clientPhone: phone.trim(),
        clientEmail: (email && email.trim()) ? email.trim() : `${phone.replace(/\D/g, '') || 'mijoz'}@advokat.uz`,
        serviceId: Number(selectedServiceObj?.id || 1),
        lawyerId: Number(selectedLawyerObj?.id || 1),
        officeId: Number(assignedOffice.id || 1),
        appointmentDate: selectedDate,
        startTime: selectedTime,
        clientReason: details.trim(),
      };

      const res = await api.post('/public/appointments', payload);
      if (res?.success) {
        setConfirmModalOpen(false);
        setBookingSuccess(res);
        showToast('Arizangiz muvaffaqiyatli tasdiqlandi va yuborildi! SMS yuborildi.', 'success');
        if (refreshUser) refreshUser();
        if (onSuccess) onSuccess(res);
      } else {
        showToast(res?.message || 'Qabulga yozilishda xatolik yuz berdi', 'error');
      }
    } catch (err) {
      showToast(err.message || 'Xatolik yuz berdi', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const selectedServiceObj = services.find((s) => String(s.id) === String(serviceId)) || services[0];
  const selectedLawyerObj = lawyers.find((l) => String(l.id) === String(lawyerId)) || lawyers[0];
  const primaryOffice = offices[0] || null;
  const officePhone = primaryOffice?.phone || '+998 (97) 325-07-70';
  
  // Real dynamic lawyer contact phone:
  // Updates instantaneously whenever the user chooses a different lawyer in the right-side dropdown
  const lawyerPhoneRaw = selectedLawyerObj?.phone ? selectedLawyerObj.phone.trim() : '';
  const currentContactPhone = lawyerPhoneRaw
    ? formatUzPhone(lawyerPhoneRaw)
    : (officePhone ? formatUzPhone(officePhone) : '+998 (97) 325-07-70');
  const currentContactPhoneClean = (lawyerPhoneRaw || officePhone || '+998973250770').replace(/[^+\d]/g, '');

  const officeLat = primaryOffice?.latitude ?? 40.160331;
  const officeLng = primaryOffice?.longitude ?? 67.827246;
  const officeMapUrl = primaryOffice?.mapUrl || primaryOffice?.googleMapsUrl || `https://www.google.com/maps/search/?api=1&query=${officeLat},${officeLng}`;
  const officeAddress = (currentLang === 'ru' ? primaryOffice?.addressRu : currentLang === 'en' ? primaryOffice?.addressEn : primaryOffice?.addressUz) || primaryOffice?.address || (currentLang === 'uz' ? "Jizzax shahar, Islom Karimov ko'chasi, Turon Bank ro'parasi" : currentLang === 'ru' ? "г. Джизак, улица Ислама Каримова, напротив Турон Банка" : "Jizzakh city, Islam Karimov street, opposite Turon Bank");

  return (
    <div id="booking-section" className="w-full relative">
      
      {/* SUCCESS CONFIRMATION MODAL / PANEL */}
      {bookingSuccess ? (
        <div className="bg-[#0b101c] border border-[#c59b27]/50 rounded-2xl p-8 sm:p-12 text-center max-w-2xl mx-auto shadow-2xl shadow-black/80 animate-in zoom-in-95">
          <div className="w-16 h-16 rounded-full bg-[#c59b27]/20 border border-[#c59b27] flex items-center justify-center text-[#c59b27] mx-auto mb-6">
            <CheckCircle2 className="w-9 h-9" />
          </div>

          <span className="text-xs uppercase tracking-[0.25em] text-[#c59b27] font-semibold block mb-2">
            {t('booking_success_title', 'ARIZA QABUL QILINDI')}
          </span>
          <h3 className="font-serif text-3xl font-bold text-slate-100 mb-4">
            {currentLang === 'uz' ? 'Uchrashuv Muvaffaqiyatli Belgilandi' : currentLang === 'ru' ? 'Консультация Успешно Запланирована' : 'Appointment Successfully Scheduled'}
          </h3>
          <p className="text-slate-300 text-sm leading-relaxed mb-6 font-light">
            {currentLang === 'uz' ? (
              <>Sizning qabul so‘rovingiz ma’muriyatga yetkazildi. Ariza ko‘rib chiqilgach, ko‘rsatilgan telefoningizga (<span className="text-slate-100 font-mono font-medium">{phone}</span>) <strong>SMS</strong> va saytdagi profilingizga tasdiq xabarnomasi keladi.</>
            ) : currentLang === 'ru' ? (
              <>Ваша заявка передана руководству коллегии. После рассмотрения на номер (<span className="text-slate-100 font-mono font-medium">{phone}</span>) поступит <strong>SMS</strong> и уведомление в личный кабинет.</>
            ) : (
              <>Your consultation request has been submitted. Upon review, an <strong>SMS</strong> will be sent to your phone (<span className="text-slate-100 font-mono font-medium">{phone}</span>) along with an in-app notification.</>
            )}
          </p>

          <div className="bg-[#060a12] border border-[#1b2b46] rounded-xl p-5 mb-8 text-left max-w-md mx-auto space-y-2.5">
            <div className="flex justify-between items-center text-xs pb-2 border-b border-[#1b2b46]">
              <span className="text-slate-400">{t('booking_ref_label', 'Noyob Bron Kodi:')}</span>
              <span className="font-mono font-bold text-[#c59b27] text-sm flex items-center">
                {bookingSuccess.bookingReference}
                <button 
                  onClick={() => {
                    navigator.clipboard.writeText(bookingSuccess.bookingReference);
                    showToast('Bron kodi nusxalandi', 'info');
                  }}
                  className="ml-2 text-slate-400 hover:text-white"
                  title="Nusxalash"
                >
                  <Copy className="w-3.5 h-3.5" />
                </button>
              </span>
            </div>
            <div className="flex justify-between items-center text-xs">
              <span className="text-slate-400">{t('date_and_time', 'Belgilangan Sana:')}</span>
              <span className="text-slate-200 font-medium">{selectedDate} ({formatTo12Hour(selectedTime)})</span>
            </div>
            <div className="flex justify-between items-center text-xs">
              <span className="text-slate-400">{t('applicant', 'Ariza Beruvchi:')}</span>
              <span className="text-slate-200 font-medium">{fullName}</span>
            </div>
            <div className="flex justify-between items-center text-xs">
              <span className="text-slate-400">{t('booking_status_label', 'Holat:')}</span>
              <span className="px-2 py-0.5 rounded text-[11px] font-semibold bg-amber-500/10 text-amber-400 border border-amber-500/30">
                {currentLang === 'uz' ? 'Kutilmoqda (PENDING)' : currentLang === 'ru' ? 'На рассмотрении (PENDING)' : 'Pending Review (PENDING)'}
              </span>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <a
              href={`/check-status?ref=${bookingSuccess.bookingReference}&contact=${encodeURIComponent(phone)}`}
              className="px-6 py-3 rounded-lg bg-gradient-to-r from-[#c59b27] to-[#b38038] hover:from-[#d4af37] hover:to-[#c59b27] text-[#070d18] text-xs font-bold shadow-lg transition-all inline-flex items-center justify-center cursor-pointer"
            >
              <ExternalLink className="w-4 h-4 mr-2" />
              {t('check_status_btn', 'Holatni onlayn kuzatish')}
            </a>
            <button
              onClick={() => {
                setBookingSuccess(null);
                setDetails('');
              }}
              className="px-6 py-3 rounded-lg border border-slate-700 hover:border-slate-500 text-xs font-medium text-slate-300 transition-colors cursor-pointer"
            >
              {t('book_another_btn', 'Yangi ariza topshirish')}
            </button>
            <a
              href="/"
              className="px-6 py-3 rounded-lg border border-[#1b2b46] bg-[#0c1424] hover:bg-[#142038] text-xs font-medium text-slate-200 transition-colors inline-flex items-center justify-center"
            >
              {t('nav_home', 'Bosh sahifaga qaytish')}
            </a>
          </div>
        </div>
      ) : (
        /* MAIN 3-PANEL LUXURY BOOKING CONTAINER */
        <div className={`bg-[#070c16]/95 border border-[#c59b27]/30 rounded-2xl ${compact ? 'p-3.5 sm:p-5' : 'p-4 sm:p-6 lg:p-7'} shadow-2xl shadow-black/80 backdrop-blur-md max-w-5xl mx-auto`}>
          {/* Top Return / Navigation Bar (Rendered only when explicitly requested and not inside modal) */}
          {showBack && !compact && (
            <div className="flex items-center justify-between pb-4 mb-5 border-b border-[#1b2b46]/70">
              <button
                type="button"
                onClick={() => {
                  if (onBack) {
                    onBack();
                  } else if (window.location.pathname === '/' && (document.getElementById('hero') || document.querySelector('header'))) {
                    const hero = document.getElementById('hero') || document.querySelector('header');
                    hero?.scrollIntoView({ behavior: 'smooth' });
                  } else if (window.history.length > 1) {
                    window.history.back();
                  } else {
                    window.location.href = '/';
                  }
                }}
                className="inline-flex items-center space-x-2 text-xs font-bold uppercase tracking-wider text-[#c59b27] hover:text-white px-3.5 py-2 rounded-lg bg-[#0e1726] hover:bg-[#15233b] border border-[#c59b27]/50 hover:border-[#c59b27] transition-all cursor-pointer shadow-md group"
              >
                <ChevronLeft className="w-4 h-4 text-[#c59b27] group-hover:-translate-x-1 transition-transform" />
                <span>{currentLang === 'uz' ? '← Orqaga qaytish' : currentLang === 'ru' ? '← Назад на главную' : '← Back to Home'}</span>
              </button>

              <div className="flex items-center space-x-3 text-xs text-slate-400">
                <span className="hidden sm:inline-flex items-center text-[11px] text-[#c59b27]">
                  <ShieldCheck className="w-3.5 h-3.5 mr-1" />
                  {currentLang === 'uz' ? 'Rasmiy ariza qabuli' : currentLang === 'ru' ? 'Официальный приём заявок' : 'Official Appointment Service'}
                </span>
                <a
                  href="/"
                  className="hover:text-white transition-colors underline decoration-slate-700 underline-offset-4"
                >
                  {currentLang === 'uz' ? 'Bosh sahifa' : currentLang === 'ru' ? 'На главную' : 'Home'}
                </a>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 sm:gap-6">
            
            {/* ================= PANEL 1: BOOK AN APPOINTMENT ================= */}
            <div className="lg:col-span-4 bg-[#080d19]/80 border border-[#17253b] hover:border-[#c59b27]/40 rounded-2xl p-4 sm:p-5 flex flex-col justify-between transition-colors duration-200 shadow-md">
              <div>
                {/* Aligned Uniform Step Header */}
                <div className="flex items-center space-x-2.5 pb-3 mb-4 border-b border-[#1b2b46]">
                  <span className="w-6 h-6 rounded-md bg-[#c59b27]/20 border border-[#c59b27]/60 text-[#c59b27] font-bold text-xs flex items-center justify-center shrink-0">1</span>
                  <h4 className="text-xs uppercase tracking-wider font-bold text-slate-200">
                    {t('book_appointment_panel', 'QABULGA YOZILISH')}
                  </h4>
                </div>

                <span className="text-[10px] uppercase tracking-[0.25em] text-[#c59b27] font-semibold block mb-1">
                  {t('firm_name_badge', "JIZZAX ADVOKATLARI")}
                </span>
                <h3 className="font-serif text-lg sm:text-xl font-bold text-slate-100 tracking-tight mb-2">
                  {t('book_appointment_sub', 'Advokatlar Qabuliga Yozilish')}
                </h3>
                <p className="text-slate-300 text-xs leading-relaxed mb-5 font-light">
                  {currentLang === 'uz' ? 'Advokatlar qabuliga yoziling va o‘z huquqlaringiz hamda manfaatlaringizni ishonchli himoya qiling.' : currentLang === 'ru' ? 'Запишитесь на приём к адвокатам и обеспечьте надежную защиту ваших законных интересов.' : 'Schedule a consultation with our advocates to safeguard your rights and business interests.'}
                </p>
              </div>

              {/* Direct Lawyer Contact Credentials (Updates dynamically on lawyer selection) */}
              <div className="space-y-3 pt-4 border-t border-[#1b2b46]/70">
                {(!isSubscribed && lawyerPhoneRaw) ? (
                  <button
                    type="button"
                    onClick={() => setSubscriptionModalOpen(true)}
                    className="flex items-center text-xs text-slate-300 hover:text-[#c59b27] transition-all group p-2.5 rounded-xl bg-[#050912] border border-[#162238] hover:border-[#c59b27]/60 shadow-sm w-full text-left cursor-pointer"
                    title="Advokat telefon raqami uchun obuna talab etiladi"
                  >
                    <div className="w-8 h-8 rounded-full bg-[#111c2e] border border-amber-500/30 flex items-center justify-center mr-2.5 shrink-0 text-amber-400">
                      <Lock className="w-3.5 h-3.5" />
                    </div>
                    <div className="flex flex-col min-w-0 flex-1">
                      <div className="flex items-center justify-between">
                        <span className="font-mono font-bold tracking-wide whitespace-nowrap text-slate-400 text-xs">
                          +998 (97) •••-••-••
                        </span>
                        <span className="text-[9px] px-1.5 py-0.5 rounded bg-amber-500/15 border border-amber-500/30 text-amber-300 font-semibold flex items-center gap-1">
                          <Lock className="w-2.5 h-2.5" /> Obuna
                        </span>
                      </div>
                      {selectedLawyerObj?.fullName && (
                        <span className="text-[10px] text-[#c59b27] truncate font-medium mt-0.5">
                          {selectedLawyerObj.fullName}
                        </span>
                      )}
                    </div>
                  </button>
                ) : (
                  <a 
                    href={`tel:${currentContactPhoneClean}`} 
                    className="flex items-center text-xs text-slate-300 hover:text-[#c59b27] transition-all group p-2.5 rounded-xl bg-[#050912] border border-[#162238] hover:border-[#c59b27]/60 shadow-sm"
                    title={selectedLawyerObj ? `${selectedLawyerObj.fullName} bilan to‘g‘ridan-to‘g‘ri bog‘lanish` : 'Ofis bilan bog‘lanish'}
                  >
                    <div className="w-8 h-8 rounded-full bg-[#111c2e] border border-slate-700 group-hover:border-[#c59b27]/70 flex items-center justify-center mr-2.5 shrink-0 text-[#c59b27] transition-colors">
                      <Phone className="w-3.5 h-3.5 group-hover:scale-110 transition-transform" />
                    </div>
                    <div className="flex flex-col min-w-0">
                      <span className="font-mono font-bold tracking-wide whitespace-nowrap text-slate-100 text-xs">
                        {currentContactPhone}
                      </span>
                      {selectedLawyerObj?.fullName && (
                        <span className="text-[10px] text-[#c59b27] truncate font-medium mt-0.5">
                          {selectedLawyerObj.fullName}
                        </span>
                      )}
                    </div>
                  </a>
                )}

                {/* 2. Manzil / Location (Clickable to Google Maps) */}
                <a
                  href={officeMapUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-start text-xs text-slate-300 hover:text-[#c59b27] transition-all group p-2.5 rounded-xl bg-[#050912] border border-[#162238] hover:border-[#c59b27]/50"
                  title="Google Maps xaritasida ofis joylashuvini ochish"
                >
                  <div className="w-7 h-7 rounded-full bg-[#111c2e] border border-slate-700 group-hover:border-[#c59b27]/70 flex items-center justify-center mr-2.5 shrink-0 text-[#c59b27] mt-0.5 transition-colors">
                    <MapPin className="w-3.5 h-3.5 group-hover:scale-110 transition-transform" />
                  </div>
                  <span className="leading-snug text-[11px]">
                    {officeAddress}<br />
                    <span className="text-[#c59b27] font-semibold text-[10px]">
                      {currentLang === 'uz' ? "O‘zbekiston Respublikasi ↗" : currentLang === 'ru' ? "Республика Узбекистан ↗" : "Republic of Uzbekistan ↗"}
                    </span>
                  </span>
                </a>

                {/* 3. Bizda konsultatsiya bepul banner (Animated Shimmering Card) */}
                <div className="relative overflow-hidden flex items-center text-xs bg-gradient-to-r from-[#c59b27]/15 via-[#c59b27]/25 to-[#c59b27]/10 border border-[#c59b27]/60 hover:border-[#c59b27] rounded-xl p-3 shadow-md shadow-[#c59b27]/15 transition-all duration-300 group">
                  <div className="absolute inset-0 bg-[#c59b27]/5 animate-pulse pointer-events-none" />
                  
                  <div className="w-7 h-7 rounded-full bg-[#c59b27]/25 border border-[#c59b27]/60 flex items-center justify-center mr-2.5 shrink-0 text-[#c59b27] group-hover:scale-110 transition-all relative z-10 shadow-sm">
                    <ShieldCheck className="w-3.5 h-3.5" />
                  </div>
                  <div className="relative z-10">
                    <span className="font-bold text-[#c59b27] block text-xs uppercase tracking-wide flex items-center gap-1.5">
                      {currentLang === 'uz' ? 'Bizda konsultatsiya bepul' : currentLang === 'ru' ? 'Консультация бесплатная' : 'Free consultation'}
                      <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-400 animate-ping" />
                    </span>
                    <span className="text-[10px] text-slate-300 block font-light leading-snug">
                      {currentLang === 'uz' ? 'Dastlabki tushuntirish va maslahat bepul taqdim etiladi' : currentLang === 'ru' ? 'Первичная консультация предоставляется бесплатно' : 'Initial guidance is provided free of charge'}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* ================= PANEL 2: CHOOSE A DATE & TIME ================= */}
            <div className="lg:col-span-4 bg-[#080d19]/80 border border-[#17253b] hover:border-[#c59b27]/40 rounded-2xl p-4 sm:p-5 flex flex-col justify-between transition-colors duration-200 shadow-md">
              <div>
                {/* Aligned Uniform Step Header */}
                <div className="flex items-center space-x-2.5 pb-3 mb-4 border-b border-[#1b2b46]">
                  <span className="w-6 h-6 rounded-md bg-[#c59b27]/20 border border-[#c59b27]/60 text-[#c59b27] font-bold text-xs flex items-center justify-center shrink-0">2</span>
                  <h4 className="text-xs uppercase tracking-wider font-bold text-slate-200">
                    {t('choose_date_time_panel', 'SANA VA VAQTNI TANLANG')}
                  </h4>
                </div>

                {/* Month Header */}
                <div className="flex items-center justify-between mb-3 px-1">
                  <span className="text-xs font-semibold text-slate-200">
                    {monthNames[currentMonth]} {currentYear}
                  </span>
                  <div className="flex space-x-1">
                    <button 
                      type="button"
                      disabled={isPrevMonthDisabled}
                      onClick={handlePrevMonth}
                      title={isPrevMonthDisabled ? 'O‘tib ketgan oylarga qaytib bo‘lmaydi' : 'Oldingi oy'}
                      className={`p-1 rounded bg-[#0b1220] border border-[#1b2b46] ${
                        isPrevMonthDisabled ? 'opacity-30 cursor-not-allowed text-slate-600' : 'hover:border-[#c59b27] text-slate-400 hover:text-white cursor-pointer'
                      }`}
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </button>
                    <button 
                      type="button"
                      onClick={handleNextMonth}
                      className="p-1 rounded bg-[#0b1220] border border-[#1b2b46] hover:border-[#c59b27] text-slate-400 hover:text-white cursor-pointer"
                    >
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                {/* Day of week headers */}
                <div className="grid grid-cols-7 gap-1 text-center mb-1.5">
                  {dayNames.map((d, i) => (
                    <span key={i} className="text-[10px] uppercase font-semibold text-slate-400">
                      {d}
                    </span>
                  ))}
                </div>

                {/* Day numbers grid */}
                <div className="grid grid-cols-7 gap-1 text-center mb-5">
                  {Array.from({ length: firstDayIndex }).map((_, i) => (
                    <div key={`empty-${i}`} className="h-8" />
                  ))}

                  {Array.from({ length: daysInMonth }).map((_, i) => {
                    const day = i + 1;
                    const dateStr = `${currentYear}-${String(currentMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
                    const todayStr = getTodayStr();
                    const isPast = dateStr < todayStr;
                    const isSelected = selectedDate === dateStr;
                    const { isWorking } = getDayScheduleInfo(currentYear, currentMonth, day);

                    return (
                      <button
                        key={day}
                        type="button"
                        disabled={isPast}
                        onClick={() => {
                          if (isPast) {
                            showToast('Bu sana o‘tib ketgan, iltimos boshqa sanani tanlang!', 'error');
                            return;
                          }
                          handleSelectDay(day);
                        }}
                        title={
                          isPast 
                            ? (currentLang === 'en' ? 'This date has passed, please choose another date' : currentLang === 'ru' ? 'Эта дата прошла, выберите другую' : 'Bu sana o‘tib ketgan, iltimos boshqa sanani tanlang')
                            : !isWorking 
                            ? `${day} — ${currentLang === 'en' ? 'Day off' : currentLang === 'ru' ? 'Выходной день' : 'Dam olish kuni'}`
                            : `${day} — ${currentLang === 'en' ? 'Working day (appointments open)' : currentLang === 'ru' ? 'Рабочий день (прием открыт)' : 'Ish kuni (qabul ochiq)'}`
                        }
                        className={`h-8 rounded-lg text-xs transition-colors flex flex-col items-center justify-center font-medium relative ${
                          isPast
                            ? 'opacity-30 cursor-not-allowed line-through text-slate-500 bg-slate-900/30'
                            : isSelected
                            ? isWorking
                              ? 'bg-[#c59b27] text-black font-bold shadow-md shadow-[#c59b27]/40 ring-2 ring-[#c59b27] cursor-pointer'
                              : 'bg-amber-600/30 text-amber-200 border border-amber-500/60 font-bold ring-2 ring-amber-500/80 cursor-pointer'
                            : !isWorking
                            ? 'text-slate-500 hover:bg-slate-800/40 hover:text-slate-300 cursor-pointer'
                            : 'text-slate-300 hover:bg-[#121c2e] hover:text-white cursor-pointer'
                        }`}
                      >
                        <span>{day}</span>
                        {!isPast && !isWorking && (
                          <span 
                            aria-hidden="true" 
                            className="w-1 h-1 rounded-full bg-slate-500/60 mt-0.5" 
                          />
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Time Slots Grid or Closed notice */}
              <div className="pt-3 border-t border-[#1b2b46]/70">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">
                    {t('available_time_slots', 'Mavjud Vaqt Oraliqlari')} ({selectedDate}):
                  </span>
                </div>

                <div className="min-h-[256px]">
                  {availableSlots.length > 0 ? (
                    <div className={`grid grid-cols-2 sm:grid-cols-3 gap-2 transition-opacity duration-150 ${loadingSlots ? 'opacity-70 pointer-events-none' : ''}`}>
                      {availableSlots.map((slot) => {
                        const isActive = selectedTime === slot.time;
                        return (
                          <button
                            key={slot.time}
                            type="button"
                            disabled={!slot.available || loadingSlots}
                            onClick={() => {
                              if (!slot.available) {
                                if (slot.reason?.includes('o‘tib ketgan')) {
                                  showToast('Bu vaqt oralig‘i o‘tib ketgan, iltimos boshqa vaqtni tanlang!', 'error');
                                }
                                return;
                              }
                              setSelectedTime(slot.time);
                            }}
                            className={`h-[36px] px-2 rounded-lg text-xs border transition-colors text-center flex items-center justify-center ${
                              isActive
                                ? 'bg-[#b38038] text-white border-[#b38038] shadow-md shadow-[#b38038]/30 font-bold cursor-pointer'
                                : slot.available
                                ? 'bg-[#050912] text-slate-300 border-[#18253b] hover:border-[#c59b27]/60 hover:text-white cursor-pointer'
                                : 'bg-[#080d17] text-slate-600 border-[#10192a] cursor-not-allowed line-through opacity-50'
                            }`}
                            title={slot.reason || undefined}
                          >
                            {slot.label}
                          </button>
                        );
                      })}
                    </div>
                  ) : loadingSlots ? (
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                      {Array.from({ length: 18 }).map((_, idx) => (
                        <div
                          key={`skel-${idx}`}
                          className="h-[36px] rounded-lg bg-[#0c1424]/80 border border-[#18253b]/60 animate-pulse flex items-center justify-center"
                        >
                          <div className="w-12 h-2.5 bg-slate-700/50 rounded" />
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="min-h-[256px] flex flex-col items-center justify-center p-4 rounded-xl bg-[#091120]/60 border border-amber-500/20 text-center space-y-2">
                      <div className="w-9 h-9 rounded-full bg-amber-500/10 border border-amber-500/30 flex items-center justify-center text-amber-400">
                        <AlertCircle className="w-4 h-4" />
                      </div>
                      <p className="text-xs font-semibold text-amber-300 max-w-[280px]">
                        {closedReason
                          ? localizeDayOffReason(closedReason, currentLang)
                          : (currentLang === 'en' ? 'No appointments available on this date.' : currentLang === 'ru' ? 'В эту дату прием не ведется.' : 'Ushbu sanada advokat qabuli mavjud emas.')}
                      </p>
                      <p className="text-[11px] text-slate-400 max-w-[260px]">
                        {currentLang === 'en'
                          ? 'Please choose another working day from the calendar.'
                          : currentLang === 'ru'
                          ? 'Пожалуйста, выберите другой рабочий день из календаря.'
                          : 'Iltimos, taqvimdan boshqa ish kunini tanlang.'}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* ================= PANEL 3: YOUR INFORMATION ================= */}
            <div className="lg:col-span-4 bg-[#080d19]/80 border border-[#17253b] hover:border-[#c59b27]/40 rounded-2xl p-4 sm:p-5 flex flex-col justify-between transition-colors duration-200 shadow-md">
              <div>
                {/* Aligned Uniform Step Header */}
                <div className="flex items-center justify-between pb-3 mb-4 border-b border-[#1b2b46]">
                  <div className="flex items-center space-x-2.5">
                    <span className="w-6 h-6 rounded-md bg-[#c59b27]/20 border border-[#c59b27]/60 text-[#c59b27] font-bold text-xs flex items-center justify-center shrink-0">3</span>
                    <h4 className="text-xs uppercase tracking-wider font-bold text-slate-200">
                      {t('your_information_panel', 'SIZNING MA’LUMOTLARINGIZ')}
                    </h4>
                  </div>
                  {!isAuthenticated && (
                    <button
                      type="button"
                      onClick={() => setAuthModalOpen(true)}
                      className="text-[11px] text-[#c59b27] hover:underline flex items-center font-medium cursor-pointer"
                    >
                      <Lock className="w-3 h-3 mr-1" />
                      {t('login_register', 'Kirish')}
                    </button>
                  )}
                </div>

                <form onSubmit={handlePreSubmit} className="space-y-3">
                  <div>
                    <input
                      type="text"
                      required
                      placeholder={t('your_name', 'To‘liq ism-familiyangiz')}
                      value={fullName}
                      onChange={(e) => setFullName(capitalizeWords(e.target.value))}
                      className="w-full px-3.5 py-2.5 bg-[#050912] border border-[#18253b] focus:border-[#c59b27] rounded-lg text-xs text-slate-100 placeholder-slate-500 focus:outline-none transition-colors"
                    />
                  </div>

                  <div>
                    <input
                      type="tel"
                      required
                      placeholder="+998 (97) 325-07-70"
                      value={phone || '+998 ()'}
                      onFocus={() => {
                        if (!phone || phone === '') setPhone('+998 ()');
                      }}
                      onChange={(e) => setPhone(formatUzPhone(e.target.value, phone))}
                      className="w-full px-3.5 py-2.5 bg-[#050912] border border-[#18253b] focus:border-[#c59b27] rounded-lg text-xs text-slate-100 placeholder-slate-500 focus:outline-none font-mono transition-colors"
                    />
                  </div>

                  {/* Practice Area selector with custom styled dropdown */}
                  <div className="relative">
                    <select
                      value={serviceId}
                      onChange={(e) => setServiceId(e.target.value)}
                      className="w-full pl-3.5 pr-8 py-2.5 bg-[#050912] border border-[#18253b] focus:border-[#c59b27] rounded-lg text-xs text-slate-100 focus:outline-none transition-colors appearance-none cursor-pointer"
                    >
                      <option value="" disabled>{t('select_service', 'Yuridik sohani tanlang')}</option>
                      {services.map((s) => (
                        <option key={s.id} value={s.id} className="bg-[#0b1220] text-slate-200">
                          {localized(s, 'title') || s.titleUz || s.title}
                        </option>
                      ))}
                    </select>
                    <ChevronDown className="w-3.5 h-3.5 text-slate-400 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                  </div>

                  {/* Lawyer Selector with formatted clean names (prevents truncating) */}
                  {lawyers.length > 1 && (
                    <div className="relative">
                      <select
                        value={lawyerId}
                        onChange={(e) => setLawyerId(e.target.value)}
                        className="w-full pl-3.5 pr-8 py-2.5 bg-[#050912] border border-[#18253b] focus:border-[#c59b27] rounded-lg text-xs text-slate-100 focus:outline-none transition-colors appearance-none cursor-pointer"
                      >
                        <option value="" disabled>{t('select_lawyer', 'Advokatni tanlang')}</option>
                        {lawyers.map((l) => {
                          const parts = (l.fullName || '').trim().split(/\s+/);
                          const shortName = parts.length >= 2 ? `${parts[0]} ${parts[1]}` : l.fullName;
                          const rawRole = localized(l, 'title') || l.titleUz || l.position || 'Advokat';
                          const role = localizeLawyerPosition(rawRole, currentLang);
                          return (
                            <option key={l.id} value={l.id} className="bg-[#0b1220] text-slate-200">
                              {shortName} ({role})
                            </option>
                          );
                        })}
                      </select>
                      <ChevronDown className="w-3.5 h-3.5 text-slate-400 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                    </div>
                  )}

                  <div>
                    <textarea
                      required
                      rows={3}
                      placeholder={t('reason_placeholder', 'Murojaat sababini qisqacha yozing (kamida 10 ta belgi)...')}
                      value={details}
                      onChange={(e) => setDetails(e.target.value)}
                      className="w-full min-h-[78px] px-3.5 py-2.5 bg-[#050912] border border-[#18253b] focus:border-[#c59b27] rounded-lg text-xs leading-relaxed text-slate-100 placeholder-slate-500 focus:outline-none transition-colors resize-none"
                    />
                    <div className="flex justify-between items-center text-[10px] text-slate-500 mt-1">
                      <span>{currentLang === 'uz' ? '* Advokatlar tomonidan o‘rganiladi' : currentLang === 'ru' ? '* Изучается адвокатом' : '* Reviewed by attorney'}</span>
                      <span className={details.length >= 10 ? 'text-emerald-400 font-semibold' : 'text-slate-500'}>
                        {details.length}/10 {currentLang === 'uz' ? 'belgi' : currentLang === 'ru' ? 'символов' : 'chars'}
                      </span>
                    </div>
                  </div>

                  {(() => {
                    const isDayOff = !loadingSlots && (availableSlots.length === 0 || !!closedReason);
                    const isSubmitDisabled = submitting || isDayOff || !selectedTime;

                    return (
                      <button
                        type="submit"
                        disabled={isSubmitDisabled}
                        className={`w-full mt-2 py-3 px-3 rounded-xl text-xs font-bold uppercase tracking-wider transition-all flex items-center justify-center text-center ${
                          isDayOff
                            ? 'bg-[#151c28] border border-[#233047] text-slate-400 opacity-90 cursor-not-allowed shadow-none'
                            : isSubmitDisabled
                            ? 'bg-[#3b2d18] border border-[#5c4623] text-amber-200/50 opacity-60 cursor-not-allowed shadow-none'
                            : 'bg-[#b38038] hover:bg-[#c59b27] text-white shadow-lg shadow-[#b38038]/30 cursor-pointer active:scale-[0.99]'
                        }`}
                      >
                        {submitting ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            {t('submitting', 'Yuborilmoqda...')}
                          </>
                        ) : isDayOff ? (
                          <div className="flex items-center justify-center text-center px-1 text-amber-400/90 font-semibold tracking-normal text-[11px] leading-tight break-words uppercase">
                            <AlertCircle className="w-4 h-4 mr-1.5 text-amber-400 shrink-0" />
                            <span>
                              {closedReason
                                ? localizeDayOffReason(closedReason, currentLang)
                                : (currentLang === 'en' ? 'Day off' : currentLang === 'ru' ? 'Выходной день' : 'Dam olish kuni')}
                            </span>
                          </div>
                        ) : (
                          t('submit_appointment_btn', 'ARIZANI YUBORISH')
                        )}
                      </button>
                    );
                  })()}
                </form>
              </div>
            </div>

          </div>
        </div>
      )}

      {/* Explicit Confirmation Dialog before final sending */}
      <BookingConfirmModal
        isOpen={confirmModalOpen}
        onClose={() => setConfirmModalOpen(false)}
        onConfirm={handleConfirmedSubmit}
        loading={submitting}
        bookingDetails={{
          serviceTitle: selectedServiceObj?.titleUz || 'Konsultatsiya',
          lawyerName: selectedLawyerObj?.fullName || 'Advokat',
          date: selectedDate,
          time: selectedTime,
          clientName: fullName,
          clientPhone: phone,
          clientEmail: email,
          clientRegion: user?.region || '',
          clientDistrict: user?.district || '',
          clientReason: details,
        }}
      />

      {/* Auth Gatekeeper Modal */}
      <AuthModal
        isOpen={authModalOpen}
        onClose={() => setAuthModalOpen(false)}
        onSuccess={() => {
          showToast('Tizimga kirdingiz. Endi arizani tasdiqlashingiz mumkin.', 'success');
        }}
      />

      {/* Subscription Modal */}
      <SubscriptionModal
        isOpen={subscriptionModalOpen}
        onClose={() => setSubscriptionModalOpen(false)}
        initialType="CLIENT"
      />

    </div>
  );
}
